<template>
    <section class="section">
      <div class="columns">
        <div class="column is-half container">
        <b-field
          type="is-blue"
          expanded
          grouped
        >
          <b-input
          data-test-id="memberAdministrationSearchInput"
          expanded
          :disabled="shouldDisplayLoading"
          :loading="shouldDisplayLoading"
          v-model="searchText"
          icon-pack="fas"
          icon="search"
          :placeholder="$t('Search')"
          />
          <p class="control" v-if="canCreateMember">
           <b-button
           :loading="shouldDisplayLoading"
            tag="router-link"
            type="is-primary"
            icon-pack="fas"
            icon-right="plus-square"
            :to="{ path: `/members/admin/create/`}"
            />
          </p>
        </b-field>
      </div>
     </div>
    <div class="container">
      <memberAdministrationDetails
        :membersList="filteredMemberList"
        :loading="shouldDisplayLoading"
        :showRelatedItems="true"
        @shop-count-clicked="shopSelected"
      />
    </div>
    <b-modal
      v-model="isVisibleRelatedShops"
      :can-cancel="true"
      has-modal-card
      width="auto"
    >
      <shopDetailsModal
        :shopsList="shopsAdministrationListDetails"
        :loading="shouldDisplayLoading"
        :titleString="titleString"
        @close="closeRelatedShopsModal"
      />
    </b-modal>
   </section>
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/app'
import type { Member, NotificationGroup, Shop, Strategy, Chain } from '@/stores/app'
import membersService from '@/services/membersService'
import notificationService from '@/services/notificationService'
import shopsService from '@/services/shopsService'
import memberAdministrationDetails from '@/components/members/memberAdministrationDetails.vue'
import shopDetailsModal from '@/components/modals/shopDetailsModal.vue'

// Store and i18n
const store = useAppStore()
const { t } = useI18n()

// Reactive data
const searchText = ref<string>('')
const isVisibleRelatedShops = ref<boolean>(false)
const relatedShops = ref<Shop[]>([])
const selectedMember = ref<Member | null>(null)

// Computed properties
const membersList = computed<Member[] | null>(() => store.getMemberList)
const notificationGroupList = computed<NotificationGroup[] | null>(() => store.getNotificationGroups)
const shopList = computed<Shop[] | null>(() => store.getAllowedShopsList)
const strategiesList = computed<Strategy[] | null>(() => store.getStrategies)
const chainsList = computed<Chain[] | null>(() => store.getChains)

const titleString = computed<string>(() => {
  return selectedMember.value
    ? `${t('Member')} ${selectedMember.value.memberId} ${selectedMember.value.memberFirstName}`
    : ''
})

const shouldDisplayLoading = computed<boolean>(() => {
  return !Array.isArray(membersList.value) || !membersList.value.length
})

const canCreateMember = computed<boolean>(() => {
  return membersList.value?.some(member => Boolean(member.canUpdateMember)) ?? false
})

interface PreparedMember {
  memberId: number
  memberFirstName: string
  memberEmail: string
  memberPhoneNumber: number
  memberPositionName: string
  memberComment: string
  groupName: string
  groupId: number
  shopsRelated: number[]
  notificationStrategy: number
  notificationStrategyName: string
  canUpdateMember: boolean
}

const membersAdministrationListDetails = computed<PreparedMember[]>(() => {
  if (!membersList.value || !notificationGroupList.value || !strategiesList.value) {
    return []
  }

  const normalizedMembers = membersList.value.map(member => ({
    memberId: member.memberId ?? 0,
    memberFirstName: member.memberFirstName ?? '',
    memberEmail: member.memberEmail ?? member.email ?? '',
    memberPhoneNumber: member.memberPhoneNumber ?? member.phoneNumber ?? 0,
    memberPositionName: member.memberPositionName ?? member.positionName ?? '',
    memberComment: member.memberComment ?? member.comment ?? '',
    groupName: member.groupName ?? '',
    groupId: member.groupId ?? 0,
    shopsRelated: member.shopsRelated ?? [],
    notificationStrategy: member.notificationStrategy ?? 0,
    notificationStrategyName: member.notificationStrategyName ?? '',
    canUpdateMember: Boolean(member.canUpdateMember),
    email: member.email,
    phoneNumber: member.phoneNumber,
    allowPhone: member.allowPhone,
    allowSms: member.allowSms,
    allowWhatsapp: member.allowWhatsapp,
    allowEmail: member.allowEmail,
    positionName: member.positionName,
    comment: member.comment
  }))

  return membersService.prepareMembersDetails(
    normalizedMembers,
    notificationGroupList.value,
    strategiesList.value
  )
})

const filteredMemberList = computed<PreparedMember[]>(() => {
  if (!membersAdministrationListDetails.value) return []
  
  return membersAdministrationListDetails.value.filter(member => {
    if (!searchText.value) return true
    
    return searchText.value
      .toUpperCase()
      .split(/[ ]+/u)
      .filter(el => el)
      .every(phrase => {
        const searchableText = [
          member.memberFirstName || '',
          member.memberId?.toString() || '',
          member.memberPhoneNumber?.toString() || '',
          member.memberEmail || ''
        ].join('').toUpperCase()
        
        return searchableText.includes(phrase)
      })
  })
})

type PreparedShop = Shop

const shopsAdministrationListDetails = computed<PreparedShop[]>(() => {
  if (!notificationGroupList.value || !membersList.value || !chainsList.value) {
    return []
  }

  const normalizedShops = relatedShops.value.map(shop => ({
    shopId: shop.shopId,
    shopName: shop.shopName,
    chainId: shop.chainId ?? 0,
    chainName: shop.chainName,
    chainShopId: shop.chainShopId ?? null,
    shoppingCenterId: shop.shoppingCenterId ?? 0,
    shoppingCenterName: shop.shoppingCenterName ?? '',
    shoppingCenterGroupId: shop.shoppingCenterGroupId ?? 0,
    shoppingCenterGroupName: shop.shoppingCenterGroupName ?? '',
    shoppingCenterShopId: shop.shoppingCenterShopId ?? '',
    sections: (shop.sections ?? []).map(section => ({
      sectionId: section.sectionId,
      sectionName: section.sectionName ?? '',
      sectionExternalId: section.sectionExternalId,
      shoppingCenterGroupId: section.shoppingCenterGroupId,
      shoppingCenterGroupName: section.shoppingCenterGroupName,
      updateSucceed: section.updateSucceed,
      relatedFailureParameter: section.relatedFailureParameter,
      failureReason: section.failureReason,
      revenueValue: section.revenueValue
    })),
    creationDate: shop.creationDate,
    deactivationDate: shop.deactivationDate,
    isDisabled: shop.isDisabled ?? false,
    canEditShop: shop.canEditShop ?? false,
    groupsRelated: shop.groupsRelated,
    membersRelated: shop.membersRelated
  }))

  const normalizedGroups = notificationGroupList.value.map(group => ({
    groupId: group.groupId,
    shopIds: group.shopIds
  }))

  const normalizedMembers = membersList.value.map(member => ({
    memberId: member.memberId ?? 0,
    groupId: member.groupId ?? 0
  }))

  return shopsService.prepareShopsData(
    normalizedShops,
    normalizedGroups,
    normalizedMembers,
    chainsList.value
  )
})

const relatedShopsModalReady = computed<boolean>(() => {
  return (
    Array.isArray(notificationGroupList.value) &&
    Array.isArray(membersList.value) &&
    Array.isArray(chainsList.value) &&
    Array.isArray(shopList.value)
  )
})

// Methods
const closeRelatedShopsModal = (): void => {
  isVisibleRelatedShops.value = false
}

const shopSelected = (memberId: number): void => {
  if (!relatedShopsModalReady.value || !membersList.value) return
  
  selectedMember.value = membersList.value.find(x => x.memberId === memberId) || null
  
  if (!selectedMember.value) return
  
  const groupId = selectedMember.value.groupId ?? 0
  const relatedNotificationGroup = notificationGroupList.value?.find(
    g => g.groupId === groupId
  )
  
  const relatedShopIds = relatedNotificationGroup?.shopIds ?? []
  relatedShops.value = shopList.value
    ? shopList.value.filter(shop => relatedShopIds.includes(shop.shopId))
    : []
    
  if (relatedShops.value.length > 0) {
    isVisibleRelatedShops.value = true
  }
}

// Lifecycle
onMounted(async () => {
  try {
    if (!notificationGroupList.value) {
      await notificationService.getNotificationGroups()
    }
    if (!membersList.value) {
      await membersService.getMembersList()
    }
    if (!shopList.value) {
      await shopsService.getAllowedShopsList()
    }
    if (!strategiesList.value) {
      notificationService.getStrategies()
    }
    if (!chainsList.value) {
      await shopsService.getChains()
    }
  } catch (error) {
    console.error('Error loading member administration data:', error)
  }
})
</script>
